/* CompuMac — Bewerbung mit Unterlagen im Kontaktformular (Inhaber 05.10.2026, „ja to all“; gebaut 07.10.2026).
   Nur von einer Stellenanzeige aus: die Karriere-Seite verlinkt /pages/kontakt?anliegen=<Betreff>&bewerbung=<Stelle>#anfrage.
   Ohne „bewerbung=…“ im Link tut dieses Skript nichts — das allgemeine Kontaktformular bleibt ohne Upload und schickt wie
   bisher an Shopify.
   Mit dem Link fragt es CompuHub (/apps/compuhub/bewerbung/v1/config → App-Proxy → Relay → CompuHub, lib/bewerbung). Nimmt
   CompuHub an (Schalter im Steuerpult → Website → Bewerbungen), erscheint das Feld „Unterlagen“: höchstens 3 Dateien, PDF,
   JPG oder PNG, je höchstens 10 MB (Grenzen kommen von CompuHub). Die Art prüft das Skript an den ersten Bytes, CompuHub
   noch einmal. Bilder werden verkleinert und neu gespeichert (ohne Standort- und Kameradaten).
   Dateien und Angaben werden hier im Browser verschlüsselt (ECDH P-256 mit Einmal-Schlüssel, HKDF-SHA256, AES-256-GCM —
   dasselbe Verfahren wie assets/cm-finance-form.js, eigener Schlüssel) und in Blöcken übertragen, weil das Relay je
   Aufruf höchstens 20 kB annimmt. Abgeschickt wird dann an CompuHub statt an Shopify; die Mail an CompuMac enthält nur
   einen Link ins Steuerpult. Gelöscht wird sechs Monate nach Eingang.
   Antwortet CompuHub nicht oder ist der Schalter aus, bleibt alles wie heute (Formular ohne Upload, an Shopify).
   Wählt jemand ein anderes Anliegen als „Bewerbung“, verschwindet der Upload und das Formular geht wieder an Shopify.
   Nichts wird im Browser gespeichert. Texte: window.cmT.bewerbung (snippets/cm-x-js-texts, Abschnitt bewerbung). */
(() => {
  'use strict';

  const E2E_INFO = 'compumac:finance:e2e:v1'; // lib/finance-requests/crypto.ts E2E_INFO
  const REQUEST_GAP_MS = 520; // Relay: 120 Aufrufe je Minute und Besucher (ivy-relay/wrangler.toml)
  const enc = new TextEncoder();
  const startedAt = Date.now();

  const cmTexts = (window.cmT && window.cmT.bewerbung) || {};
  const tx = (key, de, vars) => {
    const own = cmTexts[key];
    const text = typeof own === 'string' && own && !/^(translation missing|\[text fehlt)/i.test(own) ? own : de;
    return vars ? text.replace(/\{(\w+)\}/g, (all, name) => (name in vars ? vars[name] : all)) : text;
  };

  /* ------------------------------------------------------------ reine Helfer (auch für Tests: window.CMBewerbungCore) */

  const b64u = (bytes) => {
    let s = '';
    for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };
  const fromB64u = (s) => {
    const t = String(s).replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(t + '==='.slice((t.length + 3) % 4));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  };
  const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
  const readBytes = (blob) =>
    blob.arrayBuffer
      ? blob.arrayBuffer().then((b) => new Uint8Array(b))
      : new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(new Uint8Array(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsArrayBuffer(blob);
        });

  /** Die Art an den ersten Bytes — nicht an Endung oder Angabe des Systems (CompuHub prüft dasselbe noch einmal). */
  function sniff(bytes) {
    if (bytes.length >= 5 && String.fromCharCode(...bytes.subarray(0, 5)) === '%PDF-') return 'application/pdf';
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'image/png';
    return null;
  }

  /**
   * Kommt der Besucher von einer Stellenanzeige? Nur dann gibt es den Upload. `bewerbung` ist der Griff der Stelle (oder
   * „karriere“ für die Seite allgemein), `anliegen` der Betreff aus der Anzeige. Alles andere: null = allgemeines Formular.
   */
  function bewerbungAus(search) {
    const q = new URLSearchParams(search || '');
    const marker = String(q.get('bewerbung') || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{0,79}$/.test(marker)) return null;
    const betreff = String(q.get('anliegen') || '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
    return { stelle: marker, betreff };
  }

  /** Der Sitzungsschlüssel: ECDH mit einem Einmal-Schlüssel gegen CompuHubs öffentlichen, dann HKDF. */
  async function e2eSession(publicKeyB64) {
    const subtle = crypto.subtle;
    const server = await subtle.importKey('raw', fromB64u(publicKeyB64), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const eph = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
    const bits = await subtle.deriveBits({ name: 'ECDH', public: server }, eph.privateKey, 256);
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const hkdfKey = await subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey']);
    const aes = await subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode(E2E_INFO) }, hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
    const ephRaw = new Uint8Array(await subtle.exportKey('raw', eph.publicKey));
    return {
      eph: b64u(ephRaw),
      salt: b64u(salt),
      async encrypt(bytes, aad) {
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const ct = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(aad) }, aes, bytes));
        return { iv: b64u(iv), ct };
      },
    };
  }

  window.CMBewerbungCore = { b64u, fromB64u, sniff, bewerbungAus, e2eSession };

  /* ------------------------------------------------------------ Seite */

  const box = document.querySelector('[data-cm-bewerbung]');
  if (!box || box.dataset.ready) return;
  const ctx = bewerbungAus(location.search);
  if (!ctx) return; // allgemeines Kontaktformular: kein Upload
  box.dataset.ready = '1';
  const panel = box.closest('[data-cm-inquiry]');
  const form = box.closest('form');
  if (!panel || !form || !window.fetch || !window.crypto || !crypto.subtle) return;

  const API = box.dataset.api || '/apps/compuhub/bewerbung/v1/';
  const EN = box.dataset.lang === 'en';
  const MB = 1024 * 1024;
  const sizeText = (n) => (n >= MB ? `${(n / MB).toLocaleString(EN ? 'en-US' : 'de-DE', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(n / 1024))} kB`);

  const el = (tag, attrs = {}, ...children) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v === true ? '' : v);
    }
    for (const c of children.flat()) if (c !== null && c !== undefined && c !== false) node.append(c instanceof Node ? c : document.createTextNode(String(c)));
    return node;
  };

  let lastRequest = 0;
  async function pace() {
    const wait = lastRequest + REQUEST_GAP_MS - Date.now();
    lastRequest = Math.max(Date.now(), lastRequest + REQUEST_GAP_MS);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  }

  async function call(path, { method = 'GET', body, timeout = 20000 } = {}) {
    await pace();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    let res;
    try {
      res = await fetch(API + path + (EN ? (path.includes('?') ? '&' : '?') + 'lang=en' : ''), {
        method,
        headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch (error) {
      return { ok: false, status: 0, error: { code: 'network', message: tx('net_error', 'Keine Verbindung. Bitte prüfe dein Internet und versuch es noch einmal.') } };
    } finally {
      clearTimeout(timer);
    }
    let data = null;
    try {
      data = await res.json();
    } catch (error) {
      data = null;
    }
    if (!data || typeof data !== 'object') return { ok: false, status: res.status, error: { code: 'internal', message: tx('bad_response', 'Unerwartete Antwort.') } };
    if (!res.ok || data.ok === false) return { ok: false, status: res.status, error: data.error && typeof data.error === 'object' ? data.error : { code: 'internal', message: String(data.error || '') } };
    return { ok: true, status: res.status, data };
  }

  /** Blöcke und Abschluss wiederholen, wenn Netz oder Relay kurz aussetzen; Fehler in den Daten nicht. */
  async function callRetrying(path, body) {
    for (let attempt = 0; ; attempt++) {
      const r = await call(path, { method: 'POST', body });
      const transient = r.status === 0 || r.status === 429 || r.status === 502 || r.status === 503 || r.status === 504;
      if (r.ok || !transient || attempt >= 6) return r;
      await new Promise((res) => setTimeout(res, Math.min(15000, 800 * 2 ** attempt)));
    }
  }

  let config = null;
  let session = null;
  let formSession = '';
  let active = false; // Upload sichtbar und Absenden an CompuHub
  let submitting = false;
  let submitted = false;
  const files = [];

  const topic = form.querySelector('#CMTopic');
  const company = form.querySelector('#CMInquiryCompany');
  const help = panel.querySelector('[data-inquiry-message-help]');
  const submitButton = form.querySelector('button[type="submit"]');
  const submitLabel = submitButton ? submitButton.innerHTML : '';
  const live = () => files.filter((f) => !f.removed && f.state !== 'error');
  const pending = () => files.filter((f) => !f.removed && f.state !== 'done' && f.state !== 'error');

  let list;
  let status;
  let picker;

  function say(text, kind = 'info') {
    status.textContent = text || '';
    status.dataset.kind = kind;
    status.hidden = !text;
  }

  async function prepareImage(file) {
    let source = null;
    let width = 0;
    let height = 0;
    let bitmap = null;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      source = bitmap;
      width = bitmap.width;
      height = bitmap.height;
    } catch (error) {
      const url = URL.createObjectURL(file);
      try {
        const img = await new Promise((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = () => reject(new Error(tx('image_unreadable', 'Dieses Bild lässt sich nicht öffnen. Bitte als JPG, PNG oder PDF hochladen.')));
          i.src = url;
        });
        source = img;
        width = img.naturalWidth;
        height = img.naturalHeight;
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    }
    if (!width || !height) throw new Error(tx('image_unreadable', 'Dieses Bild lässt sich nicht öffnen. Bitte als JPG, PNG oder PDF hochladen.'));
    const encode = async (edge, q) => {
      const scale = Math.min(1, edge / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const c = canvas.getContext('2d');
      c.fillStyle = '#fff';
      c.fillRect(0, 0, w, h);
      c.drawImage(source, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
      if (!blob) throw new Error(tx('image_shrink_failed', 'Das Bild ließ sich nicht verkleinern.'));
      return readBytes(blob);
    };
    try {
      // Neu gespeichert, auch wenn es klein genug ist: so gehen Standort und Kameradaten nicht mit.
      let bytes = await encode(config.limits.imageMaxEdge, config.limits.imageQuality);
      if (bytes.length > config.limits.maxFileBytes) bytes = await encode(Math.round(config.limits.imageMaxEdge * 0.7), Math.min(config.limits.imageQuality, 0.72));
      return bytes;
    } finally {
      if (bitmap && bitmap.close) bitmap.close();
    }
  }

  function addFile(file) {
    if (live().length >= config.limits.maxFiles) return say(tx('files_max', 'Höchstens {max} Dateien – entferne eine, um sie zu ersetzen.', { max: config.limits.maxFiles }), 'error');
    say('');
    const entry = { name: file.name, state: 'prepare', removed: false, uploadId: null, promise: null };
    const bar = el('span', { class: 'cm-bw-progress__bar' });
    const line = el('span', { class: 'cm-bw-file__status', text: tx('file_preparing', 'Wird vorbereitet …') });
    const remove = el('button', { type: 'button', class: 'cm-bw-file__remove', 'aria-label': tx('file_remove_label', '{name} entfernen', { name: file.name }), text: tx('file_remove', 'Entfernen') });
    const row = el('li', { class: 'cm-bw-file is-busy' }, el('span', { class: 'cm-bw-file__name', text: file.name }), line, el('span', { class: 'cm-bw-progress', 'aria-hidden': 'true' }, bar), remove);
    list.append(row);
    files.push(entry);
    const setStatus = (text, state) => {
      line.textContent = text;
      row.classList.toggle('is-busy', state === 'busy');
      row.classList.toggle('is-done', state === 'done');
      row.classList.toggle('is-error', state === 'error');
    };
    remove.addEventListener('click', () => {
      entry.removed = true;
      row.remove();
      if (entry.uploadId) void call('uploads/discard', { method: 'POST', body: { uploadId: entry.uploadId, formSession } });
      updateButton();
    });
    entry.promise = (async () => {
      try {
        const raw = await readBytes(file);
        const type = sniff(raw);
        if (!type) throw new Error(tx('wrong_type', 'Nur PDF, JPG oder PNG – andere Formate (z. B. Word, HEIC) bitte vorher als PDF speichern.'));
        let bytes = raw;
        let mime = type;
        if (type !== 'application/pdf') {
          setStatus(tx('file_shrinking', 'Wird verkleinert …'), 'busy');
          bytes = await prepareImage(file);
          mime = 'image/jpeg';
        }
        if (bytes.length > config.limits.maxFileBytes) throw new Error(tx('too_big', 'Die Datei ist zu groß ({size}, höchstens {max}).', { size: sizeText(bytes.length), max: sizeText(config.limits.maxFileBytes) }));
        if (entry.removed) return;
        setStatus(tx('file_encrypting', 'Wird verschlüsselt …'), 'busy');
        const { iv, ct } = await session.encrypt(bytes, `cmf1|${formSession}|file`);
        const sha256 = hex(await crypto.subtle.digest('SHA-256', ct));
        const start = await callRetrying('uploads', { formSession, type: mime, bytes: ct.length, sha256, keyId: config.keyId, eph: session.eph, salt: session.salt, iv });
        if (!start.ok) throw new Error(start.error.message || tx('start_failed', 'Die Übertragung ließ sich nicht starten.'));
        entry.uploadId = start.data.uploadId;
        if (entry.removed) return void call('uploads/discard', { method: 'POST', body: { uploadId: entry.uploadId, formSession } });
        const { chunkBytes, chunks } = start.data;
        for (let i = 0; i < chunks; i++) {
          if (entry.removed) return;
          const r = await callRetrying('uploads/chunk', { uploadId: entry.uploadId, formSession, index: i, data: b64u(ct.subarray(i * chunkBytes, (i + 1) * chunkBytes)) });
          if (!r.ok) throw new Error(r.error.message || tx('chunk_failed', 'Die Übertragung ist abgebrochen.'));
          const percent = Math.round(((i + 1) / chunks) * 100);
          bar.style.width = `${percent}%`;
          setStatus(tx('file_sending', 'Wird übertragen … {percent} %', { percent }), 'busy');
        }
        const fin = await callRetrying('uploads/finish', { uploadId: entry.uploadId, formSession });
        if (!fin.ok) throw new Error(fin.error.message || tx('finish_failed', 'Die Datei kam nicht vollständig an.'));
        entry.state = 'done';
        bar.style.width = '100%';
        setStatus(tx('file_done', 'Verschlüsselt übertragen · {size}', { size: sizeText(bytes.length) }), 'done');
      } catch (error) {
        entry.state = 'error';
        setStatus(error && error.message ? error.message : tx('failed', 'Das hat nicht geklappt.'), 'error');
      } finally {
        updateButton();
      }
    })();
    updateButton();
  }

  function updateButton() {
    if (!submitButton || submitting || submitted) return;
    if (active && pending().length) submitButton.textContent = tx('submit_uploading', 'Unterlagen werden übertragen …');
    else submitButton.innerHTML = submitLabel;
  }

  function render() {
    picker = el('input', { type: 'file', id: 'CMBewerbungDateien', class: 'cm-bw-input', accept: 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png', multiple: config.limits.maxFiles > 1, 'aria-describedby': 'cm-bw-help' });
    list = el('ul', { class: 'cm-bw-list', 'aria-live': 'polite' });
    status = el('p', { class: 'cm-bw-status', role: 'status', hidden: true });
    const maxMb = Math.round(config.limits.maxFileBytes / MB);
    box.replaceChildren(
      el('p', { class: 'cm-bw-title' }, tx('title', 'Unterlagen'), el('span', { text: ` ${tx('optional', '(optional)')}` })),
      el('p', { class: 'cm-bw-help', id: 'cm-bw-help', text: tx('help', 'Lebenslauf, Zeugnisse oder Arbeitsproben: höchstens {max} Dateien als PDF, JPG oder PNG, je bis {mb} MB. Ein Bewerbungsfoto brauchen wir nicht.', { max: config.limits.maxFiles, mb: maxMb }) }),
      picker,
      el('label', { for: 'CMBewerbungDateien', class: 'cm-bw-button' }, el('span', { class: 'cm-bw-plus', 'aria-hidden': 'true', text: '+' }), tx('choose', 'Dateien auswählen')),
      list,
      status,
      el('p', { class: 'cm-bw-privacy', text: tx('privacy', 'Nachricht und Unterlagen werden schon in deinem Browser verschlüsselt und erst auf unserem Server in Friedrichshafen geöffnet. Wir löschen sie spätestens sechs Monate nach Eingang.') }),
      ...(config.testMode ? [el('p', { class: 'cm-bw-test', text: tx('test_mode', 'Testbetrieb: Deine Bewerbung wird gespeichert, aber noch nicht bearbeitet.') })] : []),
      el('div', { class: 'cm-bw-trap', 'aria-hidden': 'true' }, el('label', { for: 'CMBewerbungWebsite', text: 'Website' }), el('input', { id: 'CMBewerbungWebsite', type: 'text', tabindex: '-1', autocomplete: 'off' })),
    );
    picker.addEventListener('change', () => {
      const chosen = [...picker.files];
      picker.value = '';
      for (const file of chosen) addFile(file);
    });
    box.addEventListener('dragover', (e) => { e.preventDefault(); box.classList.add('is-drag'); });
    box.addEventListener('dragleave', () => box.classList.remove('is-drag'));
    box.addEventListener('drop', (e) => {
      e.preventDefault();
      box.classList.remove('is-drag');
      if (active) for (const file of [...(e.dataTransfer?.files || [])]) addFile(file);
    });
  }

  /** Upload an (Anliegen „Bewerbung“) oder aus (anderes Anliegen: wieder das Formular von heute, Dateien verworfen). */
  function setActive(on) {
    active = on;
    box.hidden = !on;
    if (company) {
      const wrap = company.closest('div');
      if (wrap) wrap.hidden = on; // für eine Bewerbung nicht nötig (so wenig Daten wie möglich)
    }
    if (on && help) help.textContent = tx('note', 'Erzähl uns kurz, was du mitbringst und welche Aufgabe dich interessiert. Lebenslauf und Zeugnisse kannst du unten anhängen – sie sind freiwillig.');
    if (!on) {
      for (const f of files) {
        if (f.removed) continue;
        f.removed = true;
        if (f.uploadId) void call('uploads/discard', { method: 'POST', body: { uploadId: f.uploadId, formSession } });
      }
      if (list) list.replaceChildren();
      if (status) say('');
    }
    updateButton();
  }

  function done(data) {
    submitted = true;
    const ref = String(data.reference || '');
    const success = el(
      'div',
      { class: 'cm-form-success cm-bw-success', role: 'status', tabindex: '-1' },
      el('h3', { text: tx('done_title', 'Danke – deine Bewerbung ist angekommen.') }),
      el('p', { text: tx('done_text', 'Wir lesen sie in Ruhe und melden uns per E-Mail bei dir. Deine Kennung: {reference}.', { reference: ref }) }),
      el('p', { text: tx('done_delete', 'Deine Angaben und Unterlagen löschen wir spätestens sechs Monate nach Eingang.') }),
      ...(data.testMode ? [el('p', { class: 'cm-bw-test', text: tx('done_test', 'Testbetrieb – diese Bewerbung wird nicht bearbeitet.') })] : []),
    );
    form.hidden = true;
    form.insertAdjacentElement('afterend', success);
    success.focus({ preventScroll: true });
    success.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  }

  async function submit() {
    if (submitting || submitted) return;
    if (!form.reportValidity()) return;
    submitting = true;
    if (submitButton) submitButton.disabled = true;
    try {
      if (pending().length) {
        say(tx('uploads_pending', 'Deine Unterlagen werden noch übertragen – wir senden die Bewerbung, sobald sie da sind.'));
        if (submitButton) submitButton.textContent = tx('submit_uploading', 'Unterlagen werden übertragen …');
        await Promise.all(pending().map((f) => f.promise));
      }
      if (files.some((f) => !f.removed && f.state === 'error')) {
        say(tx('file_failed', 'Eine Datei ließ sich nicht übertragen – bitte entferne sie oder wähle sie noch einmal.'), 'error');
        return;
      }
      if (submitButton) submitButton.textContent = tx('submit_sending', 'Wird verschlüsselt und gesendet …');
      const value = (sel) => ((form.querySelector(sel) || {}).value || '').trim();
      const done1 = files.filter((f) => !f.removed && f.state === 'done');
      const payload = {
        v: 1,
        angaben: { name: value('#CMName'), email: value('#CMEmail'), telefon: value('#CMPhone'), nachricht: value('#CMMessage') },
        stelle: ctx.betreff || ctx.stelle,
        dateien: done1.map((f) => ({ uploadId: f.uploadId, name: f.name })),
        elapsedMs: Date.now() - startedAt,
        website: value('#CMBewerbungWebsite'),
        page: location.pathname,
      };
      const { iv, ct } = await session.encrypt(enc.encode(JSON.stringify(payload)), `cmf1|${formSession}|payload`);
      const data = b64u(ct);
      if (data.length > 19000) {
        say(tx('too_long', 'Deine Nachricht ist zu lang – bitte kürze sie etwas.'), 'error');
        return;
      }
      const r = await callRetrying('bewerbungen', { formSession, keyId: config.keyId, eph: session.eph, salt: session.salt, iv, data, uploads: done1.map((f) => f.uploadId) });
      if (r.ok) return done(r.data);
      if (r.error.code === 'invalid_fields' && r.error.fields) {
        say(`${r.error.message || ''} ${Object.values(r.error.fields).join(' ')}`.trim(), 'error');
        return;
      }
      say(`${r.error.message || tx('failed', 'Das hat nicht geklappt.')} ${tx('still_there', 'Deine Angaben sind noch da – du kannst es gleich noch einmal versuchen.')}`, 'error');
    } catch (error) {
      say(tx('failed_retry', 'Das hat nicht geklappt. Deine Angaben sind noch da – bitte versuch es noch einmal.'), 'error');
    } finally {
      submitting = false;
      if (!submitted && submitButton) {
        submitButton.disabled = false;
        updateButton();
      }
    }
  }

  async function start() {
    const r = await call('config', { timeout: 9000 });
    if (!r.ok || !r.data.publicKey || !r.data.limits) return; // aus oder nicht erreichbar: Formular wie heute
    config = r.data;
    try {
      session = await e2eSession(config.publicKey);
    } catch (error) {
      return;
    }
    formSession = b64u(crypto.getRandomValues(new Uint8Array(16)));
    // Stylesheet erst jetzt: wer ohne Stellenanzeige kommt, lädt nichts davon.
    if (box.dataset.css && !document.querySelector('link[data-cm-bw-css]')) document.head.append(el('link', { rel: 'stylesheet', href: box.dataset.css, 'data-cm-bw-css': '' }));
    render();
    if (topic) {
      topic.value = 'Bewerbung';
      topic.dispatchEvent(new Event('change', { bubbles: true }));
      topic.addEventListener('change', () => setActive(topic.value === 'Bewerbung'));
    }
    setActive(!topic || topic.value === 'Bewerbung');
    // Abfangen vor Shopify: nur solange der Upload aktiv ist. Sonst geht das Formular wie immer an Shopify.
    form.addEventListener('submit', (event) => {
      if (!active) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void submit();
    }, true);
    window.addEventListener('beforeunload', (event) => {
      if (submitted || !active) return;
      if (pending().length || submitting) {
        event.preventDefault();
        event.returnValue = '';
      }
    });
  }

  void start();
})();
